import config from 'config';
import type { BackendModule, ReadCallback } from 'i18next';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import moment from 'moment';
import { initReactI18next } from 'react-i18next';

import './moment.config';
import {
  LANGUAGE_LOADERS,
  type LanguageResources,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from './locales/loaders';
import { NAMESPACES } from './locales/namespaces';

export const FALLBACK_LANGUAGE = 'en';

const syncMomentLocale = (language?: string) => {
  moment.locale(language ?? FALLBACK_LANGUAGE);
};

// Cache the per-language dynamic import so a whole language loads as ONE chunk
// and every namespace read resolves from that single module.
const languageCache = new Map<string, Promise<Partial<LanguageResources>>>();

const loadLanguage = (
  language: string,
): Promise<Partial<LanguageResources>> => {
  const loader = LANGUAGE_LOADERS[language as SupportedLanguage];
  if (!loader) {
    return Promise.resolve({});
  }
  let cached = languageCache.get(language);
  if (!cached) {
    cached = loader().then((module) => module.default);
    languageCache.set(language, cached);
  }
  return cached;
};

/**
 * Minimal i18next backend that serves translations from local, per-language
 * async chunks via dynamic import(). No network and no extra dependency: the
 * chunks are bundled with the app, so this works offline on Capacitor. Replaces
 * the previous eager, statically-imported `resources` object (GF-204) so only
 * the active language (plus the fallback) ships on startup instead of all of
 * en + fr in the main chunk.
 */
const localChunkBackend: BackendModule = {
  type: 'backend',
  init: () => {},
  read: (language: string, namespace: string, callback: ReadCallback) => {
    loadLanguage(language)
      .then((resources) =>
        callback(null, resources[namespace as keyof LanguageResources] ?? {}),
      )
      .catch((error) => callback(error, false));
  },
};

let initPromise: Promise<unknown> | undefined;

/**
 * Initialise i18n and resolve once the active language's namespaces are loaded.
 * Both entry points (web `src/index.jsx`, mobile `src/mobile/index.tsx`) MUST
 * await this before rendering so no translated UI paints raw keys — including
 * the out-of-component `t()` calls that run right after bootstrap. Idempotent.
 */
export const initI18n = (): Promise<unknown> => {
  if (!initPromise) {
    initPromise = i18n
      .use(localChunkBackend)
      .use(LanguageDetector)
      .use(initReactI18next)
      .init({
        debug: config.env === 'development',
        detection: {
          lookupCookie: 'hiboo_i18next',
          lookupLocalStorage: 'hiboo_i18nextLng',
        },
        fallbackLng: FALLBACK_LANGUAGE,
        supportedLngs: [...SUPPORTED_LANGUAGES],
        ns: [...NAMESPACES],
        defaultNS: 'translation',
        interpolation: {
          escapeValue: false, // not needed for react as it escapes by default
        },
        load: 'languageOnly', // en-US -> en
        react: {
          useSuspense: false,
        },
      })
      .then(() => {
        i18n.services.formatter?.add('lowercase', (value) =>
          value?.toLowerCase(),
        );
        syncMomentLocale(i18n.resolvedLanguage);
        i18n.on('languageChanged', syncMomentLocale);
      });
  }
  return initPromise;
};

export default i18n;
